const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{

    /* Skeleton tone uses border as base to work across themes */
    --ctl-skel-a: color-mix(in srgb, var(--ctl-color-border) 58%, transparent);
    --ctl-skel-b: color-mix(in srgb, var(--ctl-color-border) 30%, transparent);

    display: inline-block;
    vertical-align: middle;
  }

  .wrap{
    display:block;
  }

  .bar{
    display:block;
    width: var(--w, 100%);
    height: var(--h, 14px);
    border-radius: var(--r, var(--ctl-radius-md));
    background: linear-gradient(90deg, var(--ctl-skel-a), var(--ctl-skel-b), var(--ctl-skel-a));
    background-size: 200% 100%;
  }

  :host([animated]) .bar{
    animation: ctlSk 1.15s ease-in-out infinite;
  }

  @keyframes ctlSk{
    0%{ background-position: 0% 0; }
    100%{ background-position: 200% 0; }
  }

  /* Variants */
  :host([variant="rect"]) .bar{
    --r: var(--ctl-radius-lg);
    --h: var(--h, 14px);
  }

  :host([variant="circle"]) .bar{
    --r: 999px;
    /* circle needs square; default to 32px unless overridden */
    --w: var(--w, 32px);
    --h: var(--h, 32px);
  }

  /* Text default: slightly smaller + rounded */
  :host([variant="text"]) .bar{
    --h: var(--h, 12px);
    --r: 999px;
  }

  /* Multi-line text stack (lines attr) */
  .lines{
    display:flex;
    flex-direction: column;
    gap: var(--ctl-space-2);
  }

  .line:last-child{
    /* last line looks more natural */
    width: var(--last-w, 72%);
  }
</style>

<div class="wrap" aria-hidden="true">
  <div id="single" class="bar"></div>
  <div id="multi" class="lines" hidden></div>
</div>
`;

function clampInt(n, a, b){
  const x = Number(n);
  if (!Number.isFinite(x)) return a;
  return Math.max(a, Math.min(b, Math.floor(x)));
}

export class CtlSkeleton extends HTMLElement {
  static get observedAttributes(){
    return ["variant", "width", "height", "lines", "animated"];
  }

  constructor(){
    super();
    this.attachShadow({ mode:"open" }).appendChild(template.content.cloneNode(true));
    this._single = this.shadowRoot.getElementById("single");
    this._multi = this.shadowRoot.getElementById("multi");
  }

  connectedCallback(){
    // Visual only: hide from SRs
    if (!this.hasAttribute("aria-hidden")) this.setAttribute("aria-hidden", "true");

    // defaults
    if (!this.hasAttribute("variant")) this.setAttribute("variant", "rect");
    if (!this.hasAttribute("animated")) this.setAttribute("animated", "");

    this._sync();
  }

  attributeChangedCallback(){
    this._sync();
  }

  // attrs
  get variant(){ return this.getAttribute("variant") || "rect"; }
  set variant(v){ this.setAttribute("variant", String(v || "rect")); }

  get width(){ return this.getAttribute("width") || ""; }
  set width(v){ v == null || v === "" ? this.removeAttribute("width") : this.setAttribute("width", String(v)); }

  get height(){ return this.getAttribute("height") || ""; }
  set height(v){ v == null || v === "" ? this.removeAttribute("height") : this.setAttribute("height", String(v)); }

  get lines(){
    const v = this.getAttribute("lines");
    return v == null ? null : clampInt(v, 1, 20);
  }
  set lines(v){
    if (v == null || v === "") this.removeAttribute("lines");
    else this.setAttribute("lines", String(v));
  }

  get animated(){ return this.hasAttribute("animated"); }
  set animated(v){ v ? this.setAttribute("animated","") : this.removeAttribute("animated"); }

  _sync(){
    // normalize variant
    const v = this.variant;
    if (!["text","circle","rect"].includes(v)) this.setAttribute("variant", "rect");

    // handle animated default: present => true, missing => false
    // (keeps HTML boolean attribute semantics)
    // If user sets animated="false", attribute still exists; treat as enabled.
    // To disable, remove attribute.
    // We keep as-is.

    // apply width/height using CSS variables (safe for any unit)
    const w = this.width;
    const h = this.height;

    if (w) this.style.setProperty("--w", w);
    else this.style.removeProperty("--w");

    if (h) this.style.setProperty("--h", h);
    else this.style.removeProperty("--h");

    // multiline only for text
    const lines = this.lines;
    const multi = (v === "text" && lines && lines > 1);

    this._single.hidden = multi;
    this._multi.hidden = !multi;

    if (multi) {
      // render lines with slightly varying widths for realism
      this._multi.innerHTML = "";
      for (let i = 0; i < lines; i++) {
        const div = document.createElement("div");
        div.className = "bar line";

        // width variance: 100%, 96%, 92%..., last line shorter
        const widthPct = i === lines - 1 ? 72 : (100 - Math.min(8, i * 2));
        div.style.width = widthPct + "%";
        this._multi.appendChild(div);
      }
    }
  }
}

customElements.define("ctl-skeleton", CtlSkeleton);
